import React, { useMemo } from "react";
import styled, { css } from "styled-components";
import Skeleton from "antd/es/skeleton";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import Panel from "common/NewLook/Panel.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import {
  FinanceCurrency,
  FinanceDirection,
  FinanceEntriesFilter,
  FinanceSummaryRow,
} from "features/finance/types";
import {
  formatMoney,
  formatPercent,
  getExpenseBreakdown,
  getIncomeBreakdown,
} from "features/finance/utils";
import { CATEGORY_TO_LABEL, SOURCE_TO_LABEL } from "features/finance/constants";

interface FinanceBreakdownProps {
  rows: FinanceSummaryRow[];
  currency: FinanceCurrency;
  loading?: boolean;
  filter: FinanceEntriesFilter;
  onSelect: (filter: FinanceEntriesFilter) => void;
}

interface BreakdownRowProps {
  label: string;
  total: number;
  share: number;
  currency: FinanceCurrency;
  direction: FinanceDirection;
  selected: boolean;
  nested?: boolean;
  onClick: () => void;
}

const FinanceBreakdown = ({
  rows,
  currency,
  loading,
  filter,
  onSelect,
}: FinanceBreakdownProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const expenses = useMemo(() => getExpenseBreakdown(rows), [rows]);
  const income = useMemo(() => getIncomeBreakdown(rows), [rows]);

  const isSelected = (candidate: FinanceEntriesFilter) =>
    candidate.direction === filter.direction &&
    candidate.category === filter.category &&
    candidate.source === filter.source;

  const toggle = (candidate: FinanceEntriesFilter) =>
    onSelect(isSelected(candidate) ? {} : candidate);

  const renderBody = (isEmpty: boolean, content: React.ReactNode) => {
    if (loading) return <Skeleton active={true} paragraph={{ rows: 5 }} />;
    if (isEmpty) return <Empty>{t("finance.breakdown.empty")}</Empty>;
    return content;
  };

  return (
    <Grid>
      <Panel
        title={t("finance.breakdown.expenses_title")}
        extra={<Hint>{t("finance.breakdown.hint")}</Hint>}
      >
        {renderBody(
          !expenses.length,
          <List>
            {expenses.map((category) => {
              const categoryFilter = {
                direction: FinanceDirection.EXPENSE,
                category: category.category,
              };
              return (
                <React.Fragment key={category.category}>
                  <BreakdownRow
                    label={t(CATEGORY_TO_LABEL[category.category])}
                    total={category.total}
                    share={category.share}
                    currency={currency}
                    direction={FinanceDirection.EXPENSE}
                    selected={isSelected(categoryFilter)}
                    onClick={() => toggle(categoryFilter)}
                  />
                  {category.sources.length > 1 &&
                    category.sources.map((source) => {
                      const sourceFilter = {
                        ...categoryFilter,
                        source: source.source,
                      };
                      return (
                        <BreakdownRow
                          key={source.source}
                          label={t(SOURCE_TO_LABEL[source.source])}
                          total={source.total}
                          share={source.share}
                          currency={currency}
                          direction={FinanceDirection.EXPENSE}
                          selected={isSelected(sourceFilter)}
                          nested={true}
                          onClick={() => toggle(sourceFilter)}
                        />
                      );
                    })}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Panel>
      <Panel
        title={t("finance.breakdown.income_title")}
        extra={<Hint>{t("finance.breakdown.hint")}</Hint>}
      >
        {renderBody(
          !income.length,
          <List>
            {income.map((source) => {
              const sourceFilter = {
                direction: FinanceDirection.INCOME,
                source: source.source,
              };
              return (
                <BreakdownRow
                  key={source.source}
                  label={t(SOURCE_TO_LABEL[source.source])}
                  total={source.total}
                  share={source.share}
                  currency={currency}
                  direction={FinanceDirection.INCOME}
                  selected={isSelected(sourceFilter)}
                  onClick={() => toggle(sourceFilter)}
                />
              );
            })}
          </List>
        )}
      </Panel>
    </Grid>
  );
};

export default FinanceBreakdown;

const BreakdownRow = ({
  label,
  total,
  share,
  currency,
  direction,
  selected,
  nested,
  onClick,
}: BreakdownRowProps) => (
  <Row
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    $selected={selected}
    $nested={!!nested}
  >
    <RowLabel>{label}</RowLabel>
    <Bar>
      <BarFill
        style={{ width: `${Math.max(Math.min(share, 100), 0)}%` }}
        $isIncome={direction === FinanceDirection.INCOME}
      />
    </Bar>
    <RowShare>{formatPercent(share)}</RowShare>
    <RowAmount>{formatMoney(total, currency)}</RowAmount>
  </Row>
);

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  gap: 16px;
  align-items: start;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Hint = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @media (max-width: 700px) {
    display: none;
  }
`;
const List = styled.div`
  display: flex;
  flex-direction: column;
  margin: -6px -8px;
`;
const Row = styled.button<{ $selected: boolean; $nested: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(40px, 1fr) 52px minmax(
      84px,
      auto
    );
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: ${(p) => (p.$nested ? "4px 8px 4px 24px" : "8px")};
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: ${(p) => getNewLookTokens(p.theme).text};
  ${(p) =>
    p.$nested &&
    css`
      ${newLookType.meta};
      color: ${getNewLookTokens(p.theme).secondary};
    `}
  ${(p) =>
    p.$selected &&
    css`
      background: ${getNewLookTokens(p.theme).well};
      box-shadow: inset 2px 0 0 ${getNewLookTokens(p.theme).accent};
    `}
  &:hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
  }
  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr) 48px minmax(76px, auto);
    gap: 8px;
  }
`;
const RowLabel = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const Bar = styled.span`
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  background: ${(p) => getNewLookTokens(p.theme).well};
  @media (max-width: 600px) {
    display: none;
  }
`;
const BarFill = styled.span<{ $isIncome: boolean }>`
  display: block;
  height: 100%;
  border-radius: 3px;
  background: ${(p) =>
    p.$isIncome
      ? getNewLookTokens(p.theme).up
      : getNewLookTokens(p.theme).down};
  opacity: 0.75;
`;
const RowShare = styled.span`
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const RowAmount = styled.span`
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  white-space: nowrap;
`;
const Empty = styled.div`
  padding: 24px 0;
  text-align: center;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
